import React from "react";
import PropTypes from "prop-types";
import { Box, Card, CardContent, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) =>
  createStyles({
    card: {
      borderRadius: 8,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      border: "1px solid #e5e7eb",
    },
    content: {
      padding: theme.spacing(2),
      "&:last-child": {
        paddingBottom: theme.spacing(2),
      },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    title: {
      fontSize: 14,
      fontWeight: 700,
      color: "#1f2937",
    },
    subtitle: {
      fontSize: 12,
      color: "#6b7280",
      whiteSpace: "nowrap",
    },
    infoCards: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
    },
    infoCard: {
      border: "1px solid #fde68a",
      background: "linear-gradient(180deg, #fff9db 0%, #fff7ed 100%)",
      borderRadius: 12,
      padding: theme.spacing(2),
    },
    infoValue: {
      fontSize: 28,
      fontWeight: 800,
      color: "#b45309",
      lineHeight: 1,
      margin: "6px 0",
    },
    infoLabel: {
      color: "#92400e",
      fontSize: 12,
      fontWeight: 700,
    },
    infoSub: {
      color: "#a16207",
      fontSize: 11,
    },
    listHeader: {
      marginBottom: theme.spacing(1),
    },
    listTable: {
      minWidth: 320,
    },
    headerCell: {
      background: "#f9fafb",
      color: "#6b7280",
      fontWeight: 700,
      whiteSpace: "nowrap",
      borderBottom: "1px solid #e5e7eb",
    },
    bodyCell: {
      whiteSpace: "nowrap",
      borderBottom: "1px solid #f1f5f9",
      color: "#334155",
    },
    loadingWrap: {
      minHeight: 180,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  })
);

const PlotRewardCoinStatus = ({ summaryCards, holdings, title, subtitle, loading }) => {
  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <div className={classes.header}>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.subtitle}>{subtitle}</Typography>
        </div>

        <div className={classes.infoCards}>
          {loading ? (
            <Box className={classes.loadingWrap} style={{ gridColumn: "1 / -1", minHeight: 140 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            summaryCards.map((card) => (
              <div key={card.label} className={classes.infoCard}>
                <div className={classes.infoLabel}>{card.label}</div>
                <div className={classes.infoValue}>{card.value}</div>
                <div className={classes.infoSub}>{card.sub}</div>
              </div>
            ))
          )}
        </div>

        <div className={classes.listHeader}>
          <Typography variant="subtitle2">{holdings.title}</Typography>
        </div>
        {loading ? (
          <Box className={classes.loadingWrap} style={{ minHeight: 160 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table className={classes.listTable} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>ユーザー</TableCell>
                <TableCell className={classes.headerCell} align="right">保有枚数</TableCell>
                <TableCell className={classes.headerCell} align="right">取得日</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holdings.rows.map((row) => (
                <TableRow key={row.user} hover>
                  <TableCell className={classes.bodyCell} align="left">{row.user}</TableCell>
                  <TableCell className={classes.bodyCell} align="right">{row.count}</TableCell>
                  <TableCell className={classes.bodyCell} align="right">{row.acquired}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

PlotRewardCoinStatus.propTypes = {
  summaryCards: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      sub: PropTypes.string.isRequired,
    })
  ).isRequired,
  holdings: PropTypes.shape({
    title: PropTypes.string.isRequired,
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        user: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
        acquired: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  loading: PropTypes.bool,
};

PlotRewardCoinStatus.defaultProps = {
  title: "宝くじ排出状況（プロット分）",
  subtitle: "",
  loading: false,
};

export default PlotRewardCoinStatus;
